/**
 * Client (tenant) API service for both client portal and admin management.
 * Backed by the Client domain in the API (replacing the old Contractor domain).
 * Note: Admin client endpoints (list, get, create, update, delete, etc.) do NOT use region.
 */

import { buildApiUrlWithQuery, getAuthHeaders, getRegionForApi, valtFetch } from '@/utils/api'
import { readBikeNumberPlate, readBikeRegistryId, readBikeVin, withNormalizedBikeIdentityFields } from '@/utils/bikeFields'

// Base URL for client (tenant) API.
// Prefer VITE_API_URL when provided; otherwise default to hosted API.
const API_BASE = (import.meta.env.VITE_API_URL || 'https://api-v2.valt.africa/api').replace(/\/+$/, '')

function regionQuery(explicit?: string): Record<string, string> | undefined {
  const region = explicit || getRegionForApi()
  return region ? { region } : undefined
}

export class ApiError extends Error {
  status?: number
  response?: unknown

  constructor(message: string, options?: { status?: number; response?: unknown }) {
    super(message)
    this.name = 'ApiError'
    this.status = options?.status
    this.response = options?.response
  }
}

async function safeJson(res: Response): Promise<any> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

// NOTE:
// This interface historically represented a "Contractor". The backend has moved
// to a "Client" domain with a very similar shape. We keep the name here to
// avoid a large refactor in the frontend, but the data now comes from /client/*
// endpoints and logically represents a SaaS Client / Tenant.
export interface Contractor {
  id: number
  user_id?: number | null
  user?: unknown
  company_name: string
  company_registration_number?: string | null
  tax_id?: string | null
  contact_person: string
  email: string
  phone: string
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  contract_type?: string
  contract_start_date?: string | null
  contract_end_date?: string | null
  hourly_rate?: number | null
  status?: string
  is_active?: boolean
  verified?: boolean
  verified_at?: string | null
  verified_by?: number | null
  verifier?: { id: number; name: string; email: string } | null
  org_id?: string | null
  /** When false, client portal hides vehicle fleet, tracker vehicle tabs, and drivers. */
  vehicle_features_enabled?: boolean
  /** When false, client portal hides bike fleet and tracker bike tab. */
  bike_features_enabled?: boolean
  /** Primary portal hostname (from client_portal_hosts or legacy portal_domain). */
  portal_host?: string | null
  /** @deprecated Legacy alias — backend syncs from primary host. */
  portal_domain?: string | null
  custom_domains?: string[] | null
  valt_subdomain?: string | null
  primary_color?: string | null
  secondary_color?: string | null
  accent_color?: string | null
  login_hint?: string | null
  portal_dns_notes?: string | null
  metadata?: unknown[]
  created_at?: string
  updated_at?: string
}

// Same note as above: this is now effectively a "ClientLoginResponse".
export interface ContractorLoginResponse {
  token: string
  passport_token?: string
  scopes?: string[]
  user_context?: {
    contractor_id: number
    company_name: string
    email: string
    role: string
    must_change_password?: boolean
  }
  /** When true, the portal user must set a new password before accessing the app. */
  must_change_password?: boolean
  region?: string
  connection?: string
  // The new Client domain may return `client` instead of `contractor`.
  // We normalise this in the service so callers always see `contractor`.
  contractor: Contractor
}

export interface ContractorStats {
  total: number
  active: number
  verified: number
  pending: number
  by_contract_type?: Record<string, number>
}

export interface ContractorAssignedVehicle {
  id: number | string
  number_plate?: string | null
  licensePlate?: string | null
  vin?: string | null
  car_make?: string | null
  model?: string | null
  year?: string | null
  status?: string | null
  name?: string | null
  uber_vehicle?: Record<string, unknown> | null
  valt_vehicle?: Record<string, unknown> | null
  go_metro_vehicle?: Record<string, unknown> | null
  [key: string]: unknown
}

function normalizeAdminVehicleRow(row: unknown): ContractorAssignedVehicle | null {
  if (!row || typeof row !== 'object') return null
  const r = row as Record<string, unknown>
  const valtRaw = r.valt_vehicle ?? r.valtVehicle
  const valt =
    valtRaw && typeof valtRaw === 'object' ? (valtRaw as Record<string, unknown>) : r
  const goMetroRaw = r.go_metro_vehicle ?? r.goMetroVehicle
  const goMetro = goMetroRaw && typeof goMetroRaw === 'object' ? (goMetroRaw as Record<string, unknown>) : null

  const idRaw = valt.id ?? r.id ?? r.valt_vehicle_id ?? r.vehicle_id
  if (idRaw == null || idRaw === '') return null

  const plate =
    valt.number_plate ??
    valt.licensePlate ??
    goMetro?.license_plate ??
    goMetro?.licensePlate ??
    r.number_plate ??
    r.licensePlate ??
    null
  const vin = valt.vin ?? goMetro?.vin ?? r.vin ?? null
  const name = goMetro?.name ?? valt.name ?? r.name ?? null
  const status = goMetro?.status ?? valt.status ?? r.status ?? null

  return {
    ...r,
    id: idRaw as number | string,
    valt_vehicle: valt,
    ...(goMetro ? { go_metro_vehicle: goMetro } : {}),
    number_plate: plate != null ? String(plate) : null,
    licensePlate: plate != null ? String(plate) : null,
    vin: vin != null ? String(vin) : null,
    name: name != null ? String(name) : null,
    car_make: valt.car_make ?? r.car_make ?? null,
    model: valt.model ?? r.model ?? null,
    year: valt.year ?? r.year ?? null,
    status: status != null ? String(status) : null,
  }
}

function parseAdminVehicleList(data: unknown): ContractorAssignedVehicle[] {
  if (Array.isArray(data)) {
    return data.map((row) => normalizeAdminVehicleRow(row)).filter(Boolean) as ContractorAssignedVehicle[]
  }
  if (!data || typeof data !== 'object') return []

  const d = data as Record<string, unknown>
  const arrays = [d.vehicles, d.data, d.items].find(Array.isArray)
  if (Array.isArray(arrays)) {
    return arrays.map((row) => normalizeAdminVehicleRow(row)).filter(Boolean) as ContractorAssignedVehicle[]
  }

  if (d.valt_vehicle) {
    const one = normalizeAdminVehicleRow(d)
    if (one) return [one]
  }

  const client = d.client as Record<string, unknown> | undefined
  const md = client?.metadata as Record<string, unknown> | undefined
  const ids = md?.assigned_valt_vehicle_ids
  if (Array.isArray(ids)) {
    return ids
      .map((id) => normalizeAdminVehicleRow({ id, valt_vehicle: { id } }))
      .filter(Boolean) as ContractorAssignedVehicle[]
  }

  return []
}

/** Bike row assigned to a client (admin GET /clients/:id/bikes). */
export interface ContractorAssignedBike {
  deviceId?: string
  device_id?: string
  ident?: string
  name?: string
  model?: string
  status?: string
  type?: string
  number_plate?: string | null
  licensePlate?: string | null
  license_plate?: string | null
  vin?: string | null
  bike?: Record<string, unknown> | null
  [key: string]: unknown
}

function parseAdminBikeList(data: unknown): ContractorAssignedBike[] {
  const list = Array.isArray(data) ? data : (data as any)?.data || (data as any)?.bikes || []
  if (!Array.isArray(list)) return []
  return list
    .map((row: unknown) => normalizeAdminBikeRow(row))
    .filter((b): b is ContractorAssignedBike => !!b && !!getAdminBikeDeviceId(b))
}

function normalizeAdminBikeRow(row: unknown): ContractorAssignedBike | null {
  if (!row || typeof row !== 'object') return null
  const r = row as Record<string, unknown>
  const nested =
    r.bike && typeof r.bike === 'object' ? (r.bike as Record<string, unknown>) : r
  const deviceId = String(
    nested.deviceId ?? nested.device_id ?? nested.ident ?? nested.imei ?? '',
  ).trim()
  if (!deviceId) return null
  const numberPlate = readBikeNumberPlate(nested)
  const vin = readBikeVin(nested)
  return withNormalizedBikeIdentityFields({
    ...nested,
    deviceId,
    device_id: deviceId,
    ident: nested.ident != null ? String(nested.ident) : deviceId,
    name: nested.name != null ? String(nested.name) : deviceId,
    model: nested.model != null ? String(nested.model) : undefined,
    status: nested.status != null ? String(nested.status) : undefined,
    type: nested.type != null ? String(nested.type) : undefined,
    bike: r.bike && typeof r.bike === 'object' ? (r.bike as Record<string, unknown>) : null,
  })
}

export function getAdminBikeNumberPlate(bike: ContractorAssignedBike): string {
  return readBikeNumberPlate(bike)
}

export function getAdminBikeVin(bike: ContractorAssignedBike): string {
  return readBikeVin(bike)
}

export function getAdminBikeDeviceId(bike: ContractorAssignedBike): string {
  return String(bike.deviceId ?? bike.device_id ?? bike.ident ?? '').trim()
}

export function getAdminBikeRegistryId(bike: ContractorAssignedBike): number | undefined {
  return readBikeRegistryId(bike)
}

export interface GloballyAssignedBikeKeys {
  deviceIds: Set<string>
  registryIds: Set<string>
}

function assignedVehicleRowId(v: ContractorAssignedVehicle): string {
  const valt =
    v.valt_vehicle && typeof v.valt_vehicle === 'object'
      ? (v.valt_vehicle as Record<string, unknown>)
      : null
  const id = valt?.id ?? v.id ?? v.valt_vehicle_id ?? v.vehicle_id
  return id != null && id !== '' ? String(id) : ''
}

function vehicleIdsFromClientMetadata(client: Contractor): string[] {
  const raw = client.metadata
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return []
  const ids = (raw as Record<string, unknown>).assigned_valt_vehicle_ids
  if (!Array.isArray(ids)) return []
  return ids.map((id) => String(id)).filter(Boolean)
}

/** True when a registry bike is already assigned to any client. */
export function isBikeGloballyAssigned(
  bike: ContractorAssignedBike,
  keys: GloballyAssignedBikeKeys,
): boolean {
  const deviceId = getAdminBikeDeviceId(bike)
  if (deviceId && keys.deviceIds.has(deviceId)) return true
  const registryId = getAdminBikeRegistryId(bike)
  return registryId != null && keys.registryIds.has(String(registryId))
}

export interface AdminBikeWritePayload {
  device_id?: string
  deviceId?: string
  name?: string
  model?: string
  status?: string
  number_plate?: string
  licensePlate?: string
  license_plate?: string
  vin?: string
  cell?: string
  type?: string
}

function parseAdminBikeSingle(data: unknown): ContractorAssignedBike | null {
  const row = (data as any)?.bike ?? (data as any)?.data ?? data
  return normalizeAdminBikeRow(row)
}

export interface ContractorListParams {
  status?: string
  contract_type?: string
  verified?: string
  search?: string
  active?: boolean
  org_id?: string
  per_page?: number
  page?: number
}

/** Contractor applicant linked to a client (from GET /clients/:id/drivers). */
export interface DriverClientAssignment {
  clientId: number
  company_name: string
  /** client_drivers primary key — used for DELETE when changing client */
  linkId?: string | number
}

export interface CreateContractorPayload {
  company_name: string
  contact_person: string
  email: string
  phone: string
  password: string
  company_registration_number?: string
  tax_id?: string
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  contract_type?: 'full-time' | 'part-time' | 'project-based' | 'consultant'
  org_id?: string
}

export interface UpdateContractorPayload extends Partial<CreateContractorPayload> {
  status?: string
  is_active?: boolean
  vehicle_features_enabled?: boolean
  bike_features_enabled?: boolean
  /** Primary white-label portal hostname (e.g. fleet.yassir.com). */
  portal_host?: string
  /** Additional hostnames aliased to this tenant's portal. */
  custom_domains?: string[]
  valt_subdomain?: string
  primary_color?: string
  secondary_color?: string
  accent_color?: string
  login_hint?: string
  portal_dns_notes?: string
  metadata?: Record<string, unknown>
}

const buildQuery = (params: Record<string, unknown>) => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') {
      search.set(k, String(v))
    }
  })
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

/** Client (tenant) portal auth & profile */
export const contractorAuthApi = {
  async login(
    emailOrPhone: string,
    password: string,
    useEmail = true,
    portalHost?: string,
  ): Promise<ContractorLoginResponse> {
    const body: Record<string, string> = useEmail
      ? { email: emailOrPhone, password }
      : { phone: emailOrPhone, password }
    if (portalHost?.trim()) {
      body.portal_host = portalHost.trim().toLowerCase()
    }
    const res = await valtFetch(`${API_BASE}/client/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json()
    // Normalise Client → Contractor naming
    if (data && data.client && !data.contractor) {
      data.contractor = data.client
    }
    if (!res.ok) {
      throw new Error(data.error || data.message || 'Login failed')
    }
    // Some backends return HTTP 200 with { error: "..." } and no token — treat as failure
    if (!data?.token) {
      const msg =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        'Login failed'
      throw new Error(msg)
    }
    return data
  },

  async register(payload: CreateContractorPayload): Promise<ContractorLoginResponse> {
    const res = await valtFetch(`${API_BASE}/client/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await res.json()
    // Normalise Client → Contractor naming
    if (data && data.client && !data.contractor) {
      data.contractor = data.client
    }
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Registration failed')
    }
    if (!data?.token) {
      const msg =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        'Registration failed'
      throw new Error(msg)
    }
    return data
  },

  async forgotPassword(email: string): Promise<void> {
    const res = await valtFetch(`${API_BASE}/client/auth/forgot-password`, {
      skipAuth: true,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(
        (typeof data?.error === 'string' && data.error) ||
          (typeof data?.message === 'string' && data.message) ||
          'Failed to send password reset email',
      )
    }
  },

  async changePassword(payload: {
    current_password: string
    new_password: string
    new_password_confirmation: string
  }): Promise<void> {
    const res = await valtFetch(`${API_BASE}/client/auth/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(
        (typeof data?.error === 'string' && data.error) ||
          (typeof data?.message === 'string' && data.message) ||
          'Failed to change password',
      )
    }
  },

  async getProfile(token: string, region?: string): Promise<{ contractor: Contractor }> {
    const query = new URLSearchParams()
    if (region) query.set('region', region)
    const qs = query.toString()
    const endpoint = `${API_BASE}/client/profile${qs ? `?${qs}` : ''}`
    const res = await valtFetch(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(region && { 'X-Region': region }),
      },
    })
    const data = await res.json()
    if (!res.ok) {
      throw new Error(data.error || 'Failed to fetch profile')
    }
    // Backend may return { client: {...} } – normalise to { contractor }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },
}

/** Admin client (tenant) CRUD & stats */
export const contractorAdminApi = {
  async list(params: ContractorListParams = {}): Promise<Contractor[]> {
    const qs = buildQuery(params)
    const res = await valtFetch(`${API_BASE}/clients${qs}`, { headers: getAuthHeaders() })
    if (!res.ok) {
      throw new Error('Failed to fetch contractors')
    }
    const data = await res.json()
    // API may return array directly or { data } or { contractors } or { clients }
    if (Array.isArray(data)) return data
    return data.data ?? data.contractors ?? data.clients ?? []
  },

  async getById(id: number): Promise<{ contractor: Contractor } | Contractor> {
    const res = await valtFetch(`${API_BASE}/clients/${id}`, { headers: getAuthHeaders() })
    if (!res.ok) {
      throw new Error('Contractor not found')
    }
    const data = await res.json()
    const payload = data?.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data
    // Normalise possible { client: {...} } → { contractor: {...} }
    if (payload && payload.client && !payload.contractor) {
      return { contractor: payload.client as Contractor }
    }
    return payload
  },

  async getStats(): Promise<ContractorStats> {
    const res = await valtFetch(`${API_BASE}/clients/stats`, { headers: getAuthHeaders() })
    if (!res.ok) {
      throw new Error('Failed to fetch contractor stats')
    }
    return res.json()
  },

  async create(payload: CreateContractorPayload): Promise<{ contractor: Contractor }> {
    const res = await valtFetch(`${API_BASE}/clients`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to create contractor', {
        status: res.status,
        response: data,
      })
    }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },

  async update(id: number, payload: UpdateContractorPayload): Promise<{ contractor: Contractor }> {
    const res = await valtFetch(`${API_BASE}/clients/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to update contractor', {
        status: res.status,
        response: data,
      })
    }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },

  async delete(id: number): Promise<void> {
    const res = await valtFetch(`${API_BASE}/clients/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to delete contractor', {
        status: res.status,
        response: data,
      })
    }
  },

  async verify(id: number): Promise<{ contractor: Contractor }> {
    const res = await valtFetch(`${API_BASE}/clients/${id}/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to verify client', {
        status: res.status,
        response: data,
      })
    }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },

  async unverify(id: number): Promise<{ contractor: Contractor }> {
    const res = await valtFetch(`${API_BASE}/clients/${id}/unverify`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to unverify client', {
        status: res.status,
        response: data,
      })
    }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },

  /** Admin: enable portal access (typically sets `status` to active and `is_active` true). */
  async activate(id: number): Promise<{ contractor: Contractor }> {
    const res = await valtFetch(`${API_BASE}/clients/${id}/activate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to activate client', {
        status: res.status,
        response: data,
      })
    }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },

  /** Admin: disable portal access (typically sets inactive/suspended and `is_active` false). */
  async deactivate(id: number): Promise<{ contractor: Contractor }> {
    const res = await valtFetch(`${API_BASE}/clients/${id}/deactivate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError((data && (data.message || data.error)) || 'Failed to deactivate client', {
        status: res.status,
        response: data,
      })
    }
    if (data && data.client && !data.contractor) {
      return { contractor: data.client as Contractor }
    }
    return data
  },

  /**
   * Admin impersonate: get a contractor token to view the portal as that contractor.
   * Requires admin JWT. See docs/CONTRACTOR_IMPERSONATE_BACKEND.md for backend spec.
   */
  async impersonate(contractorId: number): Promise<ContractorLoginResponse> {
    const adminToken = localStorage.getItem('token')
    if (!adminToken) {
      throw new Error('Admin authentication required to impersonate')
    }
    const res = await valtFetch(`${API_BASE}/clients/${contractorId}/impersonate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
      },
    })
    const data = await res.json()
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Impersonation failed. Ensure backend supports this endpoint.')
    }
    if (data && data.client && !data.contractor) {
      data.contractor = data.client
    }
    return data
  },

  /**
   * Admin: link an existing driver (Uber driver_id) to a client/tenant.
   * POST /clients/:id/drivers/assign (alias of POST /clients/:id/drivers).
   */
  async assignDriverToClient(params: {
    clientId: number
    driverId: string
    valtVehicleId?: number
    bikeId?: number
    bikeDeviceId?: string
    region?: string
  }): Promise<{ success?: boolean; message?: string; [key: string]: unknown }> {
    const body: Record<string, unknown> = {
      mode: 'link',
      driver_id: params.driverId,
    }
    if (params.valtVehicleId != null) body.valt_vehicle_id = params.valtVehicleId
    if (params.bikeId != null && Number.isFinite(params.bikeId)) {
      body.bike_id = params.bikeId
      body.go_metro_bike_id = params.bikeId
    }
    const imei = params.bikeDeviceId != null ? String(params.bikeDeviceId).trim() : ''
    if (imei) body.bike_device_id = imei

    const res = await valtFetch(
      buildApiUrlWithQuery(`/clients/${params.clientId}/drivers/assign`, regionQuery(params.region)),
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new ApiError(data.message || data.error || 'Failed to assign driver to client', {
        status: res.status,
        response: data,
      })
    }
    return data
  },

  /**
   * Admin: remove driver↔bike link for a client.
   * DELETE /clients/:id/drivers/bikes/:bikeId or .../drivers/bike/:imei
   */
  async unassignBikeDriverFromClient(params: {
    clientId: number
    bikeId?: number
    bikeDeviceId?: string
    region?: string
  }): Promise<void> {
    const registryId = params.bikeId != null && Number.isFinite(params.bikeId) ? Number(params.bikeId) : null
    const imei = params.bikeDeviceId != null ? String(params.bikeDeviceId).trim() : ''
    let path = ''
    if (registryId != null) {
      path = `/clients/${params.clientId}/drivers/bikes/${encodeURIComponent(String(registryId))}`
    } else if (imei) {
      path = `/clients/${params.clientId}/drivers/bike/${encodeURIComponent(imei)}`
    } else {
      throw new ApiError('Invalid bike reference — provide bikeId or bikeDeviceId', { status: 400 })
    }
    const res = await valtFetch(buildApiUrlWithQuery(path, regionQuery(params.region)), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError(data?.message || data?.error || 'Failed to unassign driver from bike', {
        status: res.status,
        response: data,
      })
    }
  },

  /**
   * Admin: drivers linked to a client (GET /clients/:id/drivers).
   */
  async listDriversForClient(clientId: number, options?: { region?: string }): Promise<any[]> {
    const res = await valtFetch(buildApiUrlWithQuery(`/clients/${clientId}/drivers`, regionQuery(options?.region)), {
      headers: getAuthHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Failed to list client drivers')
    }
    if (Array.isArray(data)) return data
    return data.data ?? data.drivers ?? []
  },

  /**
   * Find which client each driver (Uber driver_id / UUID) is linked to by scanning clients' driver lists once.
   */
  async findDriverClientAssignments(driverUberIds: string[]): Promise<Record<string, DriverClientAssignment | null>> {
    const wants = new Set(driverUberIds.map((id) => String(id).trim()).filter(Boolean))
    const result: Record<string, DriverClientAssignment | null> = {}
    for (const id of wants) result[id] = null
    if (wants.size === 0) return result

    const clients = await this.list({ per_page: 500 })
    if (!Array.isArray(clients) || clients.length === 0) return result

    const rowUberId = (row: any): string | null => {
      const nested = row?.uber_driver || row?.driver
      const id = row?.driver_id ?? row?.uber_driver_id ?? nested?.uuid ?? nested?.driver_id ?? nested?.id
      return id != null && String(id).trim() !== '' ? String(id).trim() : null
    }
    const rowLinkId = (row: any): string | number | undefined => {
      const v = row?.client_driver_id ?? row?.client_drivers_id
      if (v != null && v !== '') return v
      const id = row?.id
      if (typeof id === 'number' && Number.isFinite(id)) return id
      if (typeof id === 'string' && /^\d+$/.test(id.trim())) return id.trim()
      return undefined
    }

    const concurrency = 12
    for (let i = 0; i < clients.length; i += concurrency) {
      const slice = clients.slice(i, i + concurrency)
      await Promise.all(
        slice.map(async (c: Contractor) => {
          try {
            const rows = await this.listDriversForClient(c.id)
            if (!Array.isArray(rows)) return
            for (const row of rows) {
              const rid = rowUberId(row)
              if (!rid || !wants.has(rid) || result[rid]) continue
              const linkId = rowLinkId(row)
              result[rid] = {
                clientId: c.id,
                company_name: c.company_name || `Client #${c.id}`,
                ...(linkId != null ? { linkId } : {}),
              }
            }
          } catch {
            /* ignore per-client failures (e.g. empty or forbidden) */
          }
        }),
      )
    }
    return result
  },

  /** Find which client a driver (Uber driver_id / UUID) is linked to by scanning clients' driver lists. */
  async findDriverClientAssignment(driverUberId: string): Promise<DriverClientAssignment | null> {
    const want = String(driverUberId).trim()
    if (!want) return null
    const map = await this.findDriverClientAssignments([want])
    return map[want] ?? null
  },

  /**
   * Remove a driver–client membership row (DELETE /clients/:clientId/drivers/:linkId).
   * `linkId` should be the client_drivers id from listDriversForClient, not the Uber driver UUID.
   */
  async removeDriverLinkFromClient(params: {
    clientId: number
    linkId: string | number
    region?: string
  }): Promise<void> {
    const res = await valtFetch(
      buildApiUrlWithQuery(
        `/clients/${params.clientId}/drivers/${encodeURIComponent(String(params.linkId))}`,
        regionQuery(params.region),
      ),
      { method: 'DELETE', headers: getAuthHeaders() },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new ApiError(data.message || data.error || 'Failed to remove driver from client', {
        status: res.status,
        response: data,
      })
    }
  },

  /**
   * Admin: unassign a vehicle from a driver (DELETE /clients/:id/drivers/:valtVehicleId).
   * Driver remains linked to the client.
   */
  async unassignVehicleFromClientDriver(params: {
    clientId: number
    valtVehicleId: number
    region?: string
  }): Promise<void> {
    if (!Number.isFinite(params.valtVehicleId)) {
      throw new ApiError('Invalid vehicle id.', { status: 400 })
    }
    const res = await valtFetch(
      buildApiUrlWithQuery(
        `/clients/${params.clientId}/drivers/${encodeURIComponent(String(params.valtVehicleId))}`,
        regionQuery(params.region),
      ),
      { method: 'DELETE', headers: getAuthHeaders() },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new ApiError(data.message || data.error || 'Failed to unassign vehicle from driver', {
        status: res.status,
        response: data,
      })
    }
  },

  /**
   * Admin: update client–driver link status (PATCH /clients/:id/drivers/:linkId).
   */
  async updateClientDriverStatus(params: {
    clientId: number
    linkId: string | number
    status: 'active' | 'inactive' | 'suspended'
    region?: string
  }): Promise<void> {
    const res = await valtFetch(
      buildApiUrlWithQuery(
        `/clients/${params.clientId}/drivers/${encodeURIComponent(String(params.linkId))}`,
        regionQuery(params.region),
      ),
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: params.status }),
      },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new ApiError(data.message || data.error || 'Failed to update driver status', {
        status: res.status,
        response: data,
      })
    }
  },

  /**
   * Admin: list vehicles assigned to a client/tenant.
   * Backend support expected: GET /clients/:id/vehicles?region=...
   */
  async listAssignedVehicles(clientId: number, options?: { region?: string }): Promise<ContractorAssignedVehicle[]> {
    const res = await valtFetch(buildApiUrlWithQuery(`/clients/${clientId}/vehicles`, regionQuery(options?.region)), {
      headers: getAuthHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Failed to load assigned vehicles')
    }
    let list = parseAdminVehicleList(data)
    if (list.length > 0) return list

    const detailsRes = await valtFetch(
      buildApiUrlWithQuery(`/clients/${clientId}/vehicles-details`, {
        ...regionQuery(options?.region),
        per_page: 500,
      }),
      { headers: getAuthHeaders() },
    )
    const detailsData = await detailsRes.json().catch(() => ({}))
    if (detailsRes.ok) {
      list = parseAdminVehicleList(detailsData)
      if (list.length > 0) return list
    }

    return list
  },

  /**
   * Admin: assign a valt-vehicle to a client/tenant.
   * POST /clients/:id/vehicles  { vehicle_id | valt_vehicle_id }
   */
  async assignVehicleToClient(params: {
    clientId: number
    vehicleId: number | string
    region?: string
  }): Promise<{
    success?: boolean
    message?: string
    client?: Contractor
    valt_vehicle?: Record<string, unknown>
    go_metro_vehicle?: Record<string, unknown>
    [key: string]: unknown
  }> {
    const vid = Number(params.vehicleId)
    const body: Record<string, unknown> = { vehicle_id: params.vehicleId }
    if (Number.isFinite(vid)) body.valt_vehicle_id = vid

    const res = await valtFetch(buildApiUrlWithQuery(`/clients/${params.clientId}/vehicles`, regionQuery(params.region)), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Failed to assign vehicle')
    }
    return data
  },

  /**
   * Admin: unassign a vehicle from a client/tenant.
   * Backend support expected: DELETE /clients/:id/vehicles/:vehicleId
   */
  async unassignVehicleFromClient(params: {
    clientId: number
    vehicleId: number | string
    region?: string
  }): Promise<void> {
    const pathVehicleId = encodeURIComponent(String(params.vehicleId))
    const res = await valtFetch(
      buildApiUrlWithQuery(`/clients/${params.clientId}/vehicles/${pathVehicleId}`, regionQuery(params.region)),
      { method: 'DELETE', headers: getAuthHeaders() },
    )
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || data.error || 'Failed to unassign vehicle')
    }
  },

  /**
   * Admin: list bikes assigned to a client/tenant.
   * Backend: GET /clients/:id/bikes?region=...
   */
  async listAssignedBikes(clientId: number, options?: { region?: string }): Promise<ContractorAssignedBike[]> {
    const res = await valtFetch(buildApiUrlWithQuery(`/clients/${clientId}/bikes`, regionQuery(options?.region)), {
      headers: getAuthHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Failed to load assigned bikes')
    }
    return parseAdminBikeList(data)
  },

  /**
   * Admin: list all registered bikes in the fleet registry.
   * Backend: GET /bikes?region=...
   */
  async listAllBikes(options?: { region?: string; perPage?: number }): Promise<ContractorAssignedBike[]> {
    const res = await valtFetch(
      buildApiUrlWithQuery('/bikes', {
        ...regionQuery(options?.region),
        page: 1,
        per_page: options?.perPage ?? 500,
      }),
      { headers: getAuthHeaders() },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Failed to load bikes')
    }
    return parseAdminBikeList(data)
  },

  /**
   * Admin: assign a bike to a client/tenant.
   * Backend: POST /clients/:id/bikes  { bike_id } or { device_id }
   */
  async assignBikeToClient(params: {
    clientId: number
    deviceId?: string
    bikeId?: number
    region?: string
  }): Promise<{ success?: boolean; message?: string }> {
    const body: Record<string, unknown> = {}
    if (params.bikeId != null && Number.isFinite(params.bikeId)) body.bike_id = params.bikeId
    const imei = params.deviceId != null ? String(params.deviceId).trim() : ''
    if (imei) {
      body.device_id = imei
      body.bike_device_id = imei
    }
    if (body.bike_id == null && !body.device_id) {
      throw new ApiError('Assign bike requires bike_id or device_id', { status: 400 })
    }

    const res = await valtFetch(buildApiUrlWithQuery(`/clients/${params.clientId}/bikes`, regionQuery(params.region)), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Failed to assign bike')
    }
    return data
  },

  /**
   * Admin: unassign a bike from a client/tenant.
   * Backend: DELETE /clients/:id/bikes/:deviceId
   */
  async unassignBikeFromClient(params: {
    clientId: number
    deviceId: string
    region?: string
  }): Promise<void> {
    const pathDeviceId = encodeURIComponent(String(params.deviceId).trim())
    const res = await valtFetch(
      buildApiUrlWithQuery(`/clients/${params.clientId}/bikes/${pathDeviceId}`, regionQuery(params.region)),
      { method: 'DELETE', headers: getAuthHeaders() },
    )
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || data.error || 'Failed to unassign bike')
    }
  },

  /**
   * Admin: fetch a single bike from the fleet registry.
   * Backend: GET /bikes/:deviceId?region=...
   */
  async getBike(deviceId: string, options?: { region?: string }): Promise<ContractorAssignedBike | null> {
    const id = encodeURIComponent(String(deviceId).trim())
    const res = await valtFetch(buildApiUrlWithQuery(`/bikes/${id}`, regionQuery(options?.region)), {
      headers: getAuthHeaders(),
    })
    if (res.status === 404) return null
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError(data?.message || data?.error || 'Failed to load bike', { status: res.status, response: data })
    }
    return parseAdminBikeSingle(data)
  },

  /**
   * Admin: register a bike in the fleet registry.
   * Backend: POST /bikes?region=...
   */
  async createBike(payload: AdminBikeWritePayload, options?: { region?: string }): Promise<ContractorAssignedBike> {
    const deviceId = String(payload.device_id ?? payload.deviceId ?? '').trim()
    const res = await valtFetch(buildApiUrlWithQuery('/bikes', regionQuery(options?.region)), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ...payload, device_id: deviceId, deviceId }),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError(data?.message || data?.error || 'Failed to create bike', { status: res.status, response: data })
    }
    const bike = parseAdminBikeSingle(data)
    if (!bike) throw new ApiError('Invalid bike response from server', { status: res.status, response: data })
    return bike
  },

  /**
   * Admin: update bike registry fields.
   * Backend: PATCH /bikes/:deviceId?region=...
   */
  async updateBike(
    deviceId: string,
    payload: AdminBikeWritePayload,
    options?: { region?: string },
  ): Promise<ContractorAssignedBike> {
    const id = encodeURIComponent(String(deviceId).trim())
    const res = await valtFetch(buildApiUrlWithQuery(`/bikes/${id}`, regionQuery(options?.region)), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const data = await safeJson(res)
    if (!res.ok) {
      throw new ApiError(data?.message || data?.error || 'Failed to update bike', { status: res.status, response: data })
    }
    const bike = parseAdminBikeSingle(data)
    if (!bike) throw new ApiError('Invalid bike response from server', { status: res.status, response: data })
    return bike
  },

  /**
   * Admin: remove a bike from the fleet registry.
   * Backend: DELETE /bikes/:deviceId?region=...
   */
  async deleteBike(deviceId: string, options?: { region?: string }): Promise<void> {
    const id = encodeURIComponent(String(deviceId).trim())
    const res = await valtFetch(buildApiUrlWithQuery(`/bikes/${id}`, regionQuery(options?.region)), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
    if (!res.ok) {
      const data = await safeJson(res)
      throw new ApiError(data?.message || data?.error || 'Failed to delete bike', { status: res.status, response: data })
    }
  },

  /**
   * Admin: collect valt-vehicle ids assigned to any client (excludes unassigned fleet assets).
   */
  async listGloballyAssignedVehicleIds(options?: { region?: string }): Promise<Set<string>> {
    const clients = await contractorAdminApi.list({ per_page: 500 })
    const ids = new Set<string>()

    await Promise.all(
      clients.map(async (client) => {
        for (const id of vehicleIdsFromClientMetadata(client)) ids.add(id)
        try {
          const assigned = await contractorAdminApi.listAssignedVehicles(client.id, { region: options?.region })
          for (const row of assigned) {
            const id = assignedVehicleRowId(row)
            if (id) ids.add(id)
          }
        } catch {
          /* skip client */
        }
      }),
    )

    return ids
  },

  /**
   * Admin: collect bike device/registry ids assigned to any client.
   */
  async listGloballyAssignedBikeKeys(options?: { region?: string }): Promise<GloballyAssignedBikeKeys> {
    const clients = await contractorAdminApi.list({ per_page: 500 })
    const deviceIds = new Set<string>()
    const registryIds = new Set<string>()

    await Promise.all(
      clients.map(async (client) => {
        try {
          const assigned = await contractorAdminApi.listAssignedBikes(client.id, { region: options?.region })
          for (const bike of assigned) {
            const deviceId = getAdminBikeDeviceId(bike)
            if (deviceId) deviceIds.add(deviceId)
            const registryId = getAdminBikeRegistryId(bike)
            if (registryId != null) registryIds.add(String(registryId))
          }
        } catch {
          /* skip client */
        }
      }),
    )

    return { deviceIds, registryIds }
  },
}
